import type { ParsedModification } from '../components/import/importReviewTypes';

export function newImportToken(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return `client_${Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')}`;
}

/** Excluded assemblies must not be silently recreated as parents during save. */
export function prepareImportRows(rows: ParsedModification[], excluded: Set<number>): ParsedModification[] {
  const ids = rows.map((row, i) => row.temporary_id || `item_${i + 1}`);
  const indexById = new Map(ids.map((id, i) => [id, i]));
  const excludedTitles = new Set(rows.filter((_, i) => excluded.has(i)).map(row => row.title));
  return rows.flatMap((row, index) => {
    if (excluded.has(index)) return [];
    let parent = row.parent_temporary_id || null;
    const visited = new Set<string>();
    while (parent) {
      if (visited.has(parent)) { parent = null; break; }
      visited.add(parent);
      const parentIndex = indexById.get(parent);
      if (parentIndex === undefined) { parent = null; break; }
      if (!excluded.has(parentIndex)) break;
      parent = rows[parentIndex].parent_temporary_id || null;
    }
    const parentRow = parent ? rows[indexById.get(parent)!] : null;
    return [{ ...row, temporary_id: ids[index], parent_temporary_id: parent,
      parentComponent: parentRow?.title || (excludedTitles.has(row.parentComponent || row.group_label || '') ? '' : row.parentComponent),
      group_label: parentRow?.title || (excludedTitles.has(row.group_label || '') ? '' : row.group_label),
      // Sort by resolved ancestry so promoting excluded parents stays valid.
      depth: parent ? row.depth : 0,
    }];
  });
}

export function importValidation(rows: ParsedModification[], excluded: Set<number>): string[] {
  const included = rows.filter((_, i) => !excluded.has(i));
  if (!included.length) return ['Include at least one part.'];
  const invalid = included.filter(row => !row.title.trim() || !row.category.trim());
  const badCosts = included.filter(row => row.cost != null && row.cost !== ('' as unknown as number) && (!Number.isFinite(Number(row.cost)) || Number(row.cost) < 0));
  const unresolved = included.filter(row => row.duplicate_warning && row.duplicate_resolution !== 'separate').length;
  return [...(unresolved ? [`Review ${unresolved} possible match${unresolved === 1 ? '' : 'es'}: exclude it or keep it as a separate part.`] : []), ...(invalid.length ? [`${invalid.length} part${invalid.length === 1 ? '' : 's'} need a name and category. Edit or exclude them.`] : []),
    ...(badCosts.length ? ['Correct the invalid costs or leave them blank.'] : [])];
}

const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
export function flagReviewDuplicates(rows: ParsedModification[], existing: { id: string | number; title: string; vendor?: string }[]): ParsedModification[] {
  const seen = new Set<string>();
  return rows.map(row => {
    const key = normalize(row.title);
    const inBatch = seen.has(key) && Boolean(key);
    seen.add(key);
    if (row.duplicate_resolution === 'separate') return row;
    const match = existing.find(part => normalize(part.title) === key && Boolean(key));
    return { ...row, duplicate_warning: row.duplicate_warning || inBatch || Boolean(match),
      duplicate_in_batch: inBatch || row.duplicate_in_batch, duplicate_of_id: match ? Number(match.id) : row.duplicate_of_id };
  });
}

/** Brand suggestions require the complete name in the supplied line, never a substring. */
export function sourceVendorSuggestion(row: ParsedModification, vendors: string[]): string | undefined {
  const normalizeWords = (text: string) => text.toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const source = ` ${normalizeWords(row.original_line || row.title)} `;
  return vendors.find(vendor => {
    const name = normalizeWords(vendor);
    return Boolean(name) && source.includes(` ${name} `);
  });
}
